/**
 * Tournament Service
 * Handles all tournament-related database operations
 */

import { supabase } from '../utils/supabase';
import { generateRoundRobinMatches, generateGroupNames, distributeTeamsIntoGroups, distributeTeamsIntoGroupsRandom } from '../utils/tournamentMatchGeneration';
import { generateKnockoutBracket } from '../utils/tournamentBracketGeneration';
import { getQualifiedTeams } from '../utils/tournamentStandings';
import { entryPlayersForSessionGame } from '../utils/tournamentFormat';
import { createGuest } from './guestService';
import { getAllGroupStandings } from './tournamentStandingsService';

export const TOURNAMENT_NAME_MAX_LENGTH = 80;

const validateTournamentName = (name) => {
  const trimmed = (name || '').trim();
  if (!trimmed) {
    throw new Error('Tournament name is required');
  }
  if (trimmed.length > TOURNAMENT_NAME_MAX_LENGTH) {
    throw new Error(`Tournament name must be ${TOURNAMENT_NAME_MAX_LENGTH} characters or fewer`);
  }
  return trimmed;
};

/**
 * Get best_of (1, 3, or 5) for a given round. Uses round_formats override or default_best_of.
 */
export const getBestOfForRound = (tournament, round) => {
  const override = tournament?.round_formats?.[round];
  if (override !== undefined && override !== null && [1, 3, 5].includes(Number(override))) {
    return Number(override);
  }
  const defaultBest = tournament?.default_best_of ?? 1;
  return [1, 3, 5].includes(Number(defaultBest)) ? Number(defaultBest) : 1;
};

/**
 * Create a new tournament
 */
export const createTournament = async (clubId, data) => {
  const validatedName = validateTournamentName(data.name);
  const { data: tournament, error } = await supabase
    .from('tournaments')
    .insert({
      club_id: clubId,
      season_id: data.seasonId || null,
      name: validatedName,
      game_type_id: data.gameTypeId,
      default_best_of: data.defaultBestOf || 1,
      number_of_courts: data.numberOfCourts || 2,
      points_target: data.pointsTarget ?? 21,
      score_validation: data.scoreValidation || 'standard',
      tournament_date: data.tournamentDate || null,
      start_time: data.startTime || null,
      end_time: data.endTime || null,
      location: data.location?.trim() || null,
      ranking_order: data.rankingOrder || ['wins', 'point_difference', 'points_scored', 'head_to_head'],
      tiebreaker_order: data.tiebreakerOrder || ['point_difference', 'head_to_head', 'random'],
      created_by: data.createdBy,
      status: 'draft'
    })
    .select()
    .single();

  if (error) throw error;
  return tournament;
};

/**
 * Get tournament with all related data
 */
export const getTournament = async (tournamentId) => {
  const { data: tournament, error } = await supabase
    .from('tournaments')
    .select(`
      *,
      game_type:game_type_id (
        id,
        name,
        is_doubles
      ),
      tournament_entries (
        *,
        tournament_entry_players (
          position,
          player:player_id (*)
        )
      ),
      tournament_solo_entrants (
        id,
        tournament_id,
        player_id,
        created_at,
        player:player_id (*)
      ),
      tournament_player_bands (
        tournament_id,
        player_id,
        band_code,
        created_at,
        player:player_id (*)
      ),
      tournament_groups (
        *,
        tournament_group_entries (
          entry_id,
          tournament_entries (
            *,
            tournament_entry_players (
              position,
              player:player_id (*)
            )
          )
        )
      ),
      tournament_matches (
        *,
        group:group_id (
          id,
          name
        ),
        team1:entry1_id (
          *,
          tournament_entry_players (
            position,
            player:player_id (*)
          )
        ),
        team2:entry2_id (
          *,
          tournament_entry_players (
            position,
            player:player_id (*)
          )
        ),
        winner_team:winner_entry_id (
          *,
          tournament_entry_players (
            position,
            player:player_id (*)
          )
        )
      )
    `)
    .eq('id', tournamentId)
    .is('deleted_at', null)
    .single();

  if (error) throw error;
  return tournament;
};

/**
 * Update tournament
 */
export const updateTournament = async (tournamentId, updates) => {
  const normalizedUpdates = { ...updates };
  if (Object.prototype.hasOwnProperty.call(normalizedUpdates, 'name')) {
    normalizedUpdates.name = validateTournamentName(normalizedUpdates.name);
  }

  const { data, error } = await supabase
    .from('tournaments')
    .update(normalizedUpdates)
    .eq('id', tournamentId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Validate that a format downgrade won't cause data loss.
 * Returns { allowed: true } or { allowed: false, message: string }.
 */
export const validateFormatDowngrade = (tournament, round, newBestOf) => {
  const value = Number(newBestOf);
  if (![1, 3, 5].includes(value)) return { allowed: true };

  const matches = (tournament.tournament_matches || []).filter((m) => m.round === round);
  for (const match of matches) {
    const currentBestOf = match.best_of ?? 1;
    const gamesPlayed = match.games?.length ?? 0;
    if (value < currentBestOf && gamesPlayed > value) {
      return {
        allowed: false,
        message: `Cannot change to Best of ${value}: some matches already have ${gamesPlayed} game(s) recorded. Change would cause data loss.`
      };
    }
  }
  return { allowed: true };
};

/**
 * Update best_of for all group matches when host changes format.
 * Validates no downgrade would cause data loss before updating.
 */
export const updateGroupMatchesBestOf = async (tournamentId, bestOf, tournament) => {
  const value = Number(bestOf);
  if (![1, 3, 5].includes(value)) return;

  if (tournament) {
    const validation = validateFormatDowngrade(tournament, 'group', value);
    if (!validation.allowed) throw new Error(validation.message);
  }

  const { error } = await supabase
    .from('tournament_matches')
    .update({ best_of: value })
    .eq('tournament_id', tournamentId)
    .eq('round', 'group');

  if (error) throw error;
};

/**
 * Update best_of for a single match (per-match format override).
 * Validates no downgrade would cause data loss.
 */
export const updateMatchBestOf = async (matchId, bestOf) => {
  const value = Number(bestOf);
  if (![1, 3, 5].includes(value)) throw new Error('Invalid format. Use 1, 3, or 5.');

  const { data: games } = await supabase
    .from('session_games')
    .select('id')
    .eq('tournament_match_id', matchId);

  const gamesCount = games?.length ?? 0;
  if (gamesCount > value) {
    throw new Error(
      `Cannot change to Best of ${value}: this match already has ${gamesCount} game(s) recorded.`
    );
  }

  const { data: updated, error } = await supabase
    .from('tournament_matches')
    .update({ best_of: value })
    .eq('id', matchId)
    .select()
    .single();

  if (error) throw error;
  return updated;
};

const selectEntryWithPlayers = `
  *,
  tournament_entry_players (
    position,
    player:player_id (*)
  )
`;

/**
 * Add a doubles entry (two players) in one DB transaction.
 */
export const addTeam = async (tournamentId, player1Id, player2Id) => {
  const { data: entryId, error } = await supabase.rpc('create_tournament_entry_with_players', {
    p_tournament_id: tournamentId,
    p_player_ids: [player1Id, player2Id]
  });

  if (error) throw error;

  const { data, error: fetchError } = await supabase
    .from('tournament_entries')
    .select(selectEntryWithPlayers)
    .eq('id', entryId)
    .single();

  if (fetchError) throw fetchError;
  return data;
};

/**
 * Add a singles entry (one player).
 */
export const addSinglesEntry = async (tournamentId, playerId) => {
  const { data: entryId, error } = await supabase.rpc('create_tournament_entry_with_players', {
    p_tournament_id: tournamentId,
    p_player_ids: [playerId]
  });

  if (error) throw error;

  const { data, error: fetchError } = await supabase
    .from('tournament_entries')
    .select(selectEntryWithPlayers)
    .eq('id', entryId)
    .single();

  if (fetchError) throw fetchError;
  return data;
};

/**
 * Register a player in the solo pool (not on a team yet). DB rejects if already on a team.
 */
export const addSoloEntrant = async (tournamentId, playerId) => {
  const { data, error } = await supabase
    .from('tournament_solo_entrants')
    .insert({ tournament_id: tournamentId, player_id: playerId })
    .select(`
      *,
      player:player_id (*)
    `)
    .single();

  if (error) throw error;
  return data;
};

/**
 * Remove a player from the solo pool
 */
export const removeSoloEntrant = async (tournamentId, playerId) => {
  const { error } = await supabase
    .from('tournament_solo_entrants')
    .delete()
    .eq('tournament_id', tournamentId)
    .eq('player_id', playerId);

  if (error) throw error;
};

/**
 * Atomically pair two solo entrants into one team (RPC deletes solos then inserts team).
 */
export const pairSoloPlayers = async (tournamentId, player1Id, player2Id) => {
  const { data: teamId, error } = await supabase.rpc('pair_tournament_solo_players', {
    p_tournament_id: tournamentId,
    p_player1_id: player1Id,
    p_player2_id: player2Id
  });

  if (error) throw error;

  const { data: team, error: teamError } = await supabase
    .from('tournament_entries')
    .select(selectEntryWithPlayers)
    .eq('id', teamId)
    .single();

  if (teamError) throw teamError;
  return team;
};

/**
 * Create many teams from [[player1Id, player2Id], ...] in one transaction.
 * Removes each pair from the solo pool before insert (no-op if not solo).
 */
export const bulkCreateTournamentTeamsFromPairs = async (tournamentId, pairs) => {
  if (!Array.isArray(pairs) || pairs.length === 0) {
    throw new Error('At least one pair is required');
  }
  const json = pairs.map((p) => {
    if (!Array.isArray(p) || p.length !== 2) {
      throw new Error('Each pair must be [player1Id, player2Id]');
    }
    return [p[0], p[1]];
  });

  const { data, error } = await supabase.rpc('bulk_create_tournament_teams_from_pairs', {
    p_tournament_id: tournamentId,
    p_pairs: json
  });

  if (error) throw error;
  return typeof data === 'number' ? data : parseInt(data, 10);
};

/**
 * Set or update a player's pairing band for a tournament
 */
export const upsertTournamentPlayerBand = async (tournamentId, playerId, bandCode) => {
  const code = (bandCode || '').trim();
  if (!code) throw new Error('Band code is required');

  const { data, error } = await supabase
    .from('tournament_player_bands')
    .upsert(
      { tournament_id: tournamentId, player_id: playerId, band_code: code },
      { onConflict: 'tournament_id,player_id' }
    )
    .select(`
      *,
      player:player_id (*)
    `)
    .single();

  if (error) throw error;
  return data;
};

/**
 * Clear band assignment for a player in a tournament
 */
export const clearTournamentPlayerBand = async (tournamentId, playerId) => {
  const { error } = await supabase
    .from('tournament_player_bands')
    .delete()
    .eq('tournament_id', tournamentId)
    .eq('player_id', playerId);

  if (error) throw error;
};

/**
 * ELO rows for club + season (for seed suggestions). Uses RLS.
 */
export const getPlayerClubEloRatingsForSeason = async (clubId, seasonId, playerIds = null) => {
  let query = supabase
    .from('player_club_elo_ratings')
    .select('player_id, club_id, season_id, mixed_elo_rating, levels_elo_rating, singles_elo_rating')
    .eq('club_id', clubId)
    .eq('season_id', seasonId);

  if (playerIds?.length) {
    query = query.in('player_id', playerIds);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

/**
 * Remove a team from tournament
 */
export const removeTeam = async (tournamentId, teamId) => {
  // Check if team is in any completed matches
  const { data: matches, error: matchError } = await supabase
    .from('tournament_matches')
    .select('id, status')
    .eq('tournament_id', tournamentId)
    .or(`entry1_id.eq.${teamId},entry2_id.eq.${teamId}`)
    .eq('status', 'completed');

  if (matchError) throw matchError;

  if (matches && matches.length > 0) {
    throw new Error('Cannot remove team with completed matches');
  }

  // Delete team (cascade will handle group assignments and matches)
  const { error } = await supabase
    .from('tournament_entries')
    .delete()
    .eq('id', teamId)
    .eq('tournament_id', tournamentId);

  if (error) throw error;
};

/**
 * Add guest and create team
 */
export const addGuestAndPair = async (tournamentId, guestData, partnerId, clubId, createdBy, seasonId) => {
  // Create guest player
  const guest = await createGuest({
    ...guestData,
    clubId,
    createdBy,
    seasonId
  });

  // Create team with guest and partner
  const team = await addTeam(tournamentId, guest.id, partnerId);
  
  return { guest, team };
};

/**
 * Configure groups (create group records).
 * Team counts do not need to divide evenly: auto-assign distributes as evenly as possible
 * (sizes differ by at most one). Each group must still have at least 2 teams for round robin.
 */
export const configureGroups = async (tournamentId, groupCount) => {
  const { data: teams, error: teamError } = await supabase
    .from('tournament_entries')
    .select('id')
    .eq('tournament_id', tournamentId);

  if (teamError) throw teamError;

  const totalTeams = teams.length;

  if (groupCount < 1) {
    throw new Error('At least 1 group is required');
  }
  if (totalTeams < groupCount) {
    throw new Error(`Cannot create ${groupCount} groups with only ${totalTeams} team(s)`);
  }
  const minTeamsInAnyGroup = Math.floor(totalTeams / groupCount);
  if (minTeamsInAnyGroup < 2) {
    const need = groupCount * 2;
    throw new Error(
      `Need at least ${need} teams for ${groupCount} groups (minimum 2 per group for round robin). You have ${totalTeams}.`
    );
  }

  // Stored value = floor average; actual group sizes may be this or this+1 when total does not divide evenly
  const teamsPerGroupStored = Math.floor(totalTeams / groupCount);

  // Delete existing groups if any
  await supabase
    .from('tournament_groups')
    .delete()
    .eq('tournament_id', tournamentId);

  // Create groups
  const groupNames = generateGroupNames(groupCount);
  const groups = [];

  for (let i = 0; i < groupCount; i++) {
    const { data: group, error } = await supabase
      .from('tournament_groups')
      .insert({
        tournament_id: tournamentId,
        name: groupNames[i],
        sort_order: i + 1
      })
      .select()
      .single();

    if (error) throw error;
    groups.push(group);
  }

  // Update tournament with group config
  await updateTournament(tournamentId, {
    group_count: groupCount,
    teams_per_group: teamsPerGroupStored,
  });

  return groups;
};

/**
 * Assign teams to groups
 */
export const assignTeamsToGroups = async (tournamentId, assignments) => {
  // assignments: { groupId: [teamId1, teamId2, ...] }
  
  // Delete existing assignments
  const { data: groups } = await supabase
    .from('tournament_groups')
    .select('id')
    .eq('tournament_id', tournamentId);

  if (groups) {
    for (const group of groups) {
      await supabase
        .from('tournament_group_entries')
        .delete()
        .eq('group_id', group.id);
    }
  }

  // Insert new assignments
  const inserts = [];
  for (const [groupId, teamIds] of Object.entries(assignments)) {
    for (const teamId of teamIds) {
      inserts.push({
        group_id: groupId,
        entry_id: teamId
      });
    }
  }

  const { error } = await supabase
    .from('tournament_group_entries')
    .insert(inserts);

  if (error) throw error;
};

/**
 * Assign every team to a group — either deterministic round-robin order (balanced) or shuffled (still balanced).
 * @param {string} tournamentId
 * @param {{ mode?: 'balanced' | 'random' }} [options]
 */
export const autoAssignTeamsToGroups = async (tournamentId, options = {}) => {
  const mode = options.mode === 'random' ? 'random' : 'balanced';

  // Get all teams and groups
  const { data: teams } = await supabase
    .from('tournament_entries')
    .select('id')
    .eq('tournament_id', tournamentId)
    .order('created_at');

  const { data: groups } = await supabase
    .from('tournament_groups')
    .select('id')
    .eq('tournament_id', tournamentId)
    .order('sort_order');

  if (!teams || !groups) throw new Error('Teams or groups not found');

  const teamIdList = teams.map((t) => t.id);
  const distribution =
    mode === 'random'
      ? distributeTeamsIntoGroupsRandom(teamIdList, groups.length)
      : distributeTeamsIntoGroups(teamIdList, groups.length);

  // Convert to assignments format
  const assignments = {};
  distribution.forEach((dist, index) => {
    assignments[groups[index].id] = dist.teamIds;
  });

  await assignTeamsToGroups(tournamentId, assignments);
};

/**
 * Clear all groups from a tournament (for reconfiguration)
 * Deletes groups; cascade removes group assignments and group matches
 */
export const clearTournamentGroups = async (tournamentId) => {
  const { error } = await supabase
    .from('tournament_groups')
    .delete()
    .eq('tournament_id', tournamentId);

  if (error) throw error;

  await updateTournament(tournamentId, {
    group_count: null,
    teams_per_group: null,
    status: 'draft'
  });
};

/**
 * Manual group configuration
 * Creates groups and assigns teams based on manual assignments
 * @param {string} tournamentId 
 * @param {number} groupCount 
 * @param {Object} teamAssignments - { teamId: groupNumber (1-based), ... }
 */
export const manualConfigureGroups = async (tournamentId, groupCount, teamAssignments) => {
  // Delete existing groups if any
  await supabase
    .from('tournament_groups')
    .delete()
    .eq('tournament_id', tournamentId);

  // Create groups
  const groupNames = generateGroupNames(groupCount);
  const groups = [];

  for (let i = 0; i < groupCount; i++) {
    const { data: group, error } = await supabase
      .from('tournament_groups')
      .insert({
        tournament_id: tournamentId,
        name: groupNames[i],
        sort_order: i + 1
      })
      .select()
      .single();

    if (error) throw error;
    groups.push(group);
  }

  // Update tournament
  await supabase
    .from('tournaments')
    .update({
      status: 'group_stage',
      group_count: groupCount
    })
    .eq('id', tournamentId);

  // Convert teamAssignments to the format expected by assignTeamsToGroups
  // teamAssignments: { teamId: groupNumber (1-based) }
  // Need to convert to: { groupId: [teamIds] }
  const assignments = {};
  groups.forEach((group, index) => {
    const groupNumber = index + 1;
    assignments[group.id] = Object.keys(teamAssignments)
      .filter(teamId => teamAssignments[teamId] === groupNumber)
      .map(teamId => teamId);
  });

  await assignTeamsToGroups(tournamentId, assignments);

  return groups;
};

/**
 * Update existing group assignments
 * Used when editing groups after they've been created
 * @param {string} tournamentId 
 * @param {Object} assignments - { groupId: [teamIds] }
 */
export const updateGroupAssignments = async (tournamentId, assignments) => {
  // Clear existing assignments
  const { data: groups } = await supabase
    .from('tournament_groups')
    .select('id')
    .eq('tournament_id', tournamentId);

  if (groups) {
    for (const group of groups) {
      await supabase
        .from('tournament_group_entries')
        .delete()
        .eq('group_id', group.id);
    }
  }

  // Insert new assignments
  const inserts = [];
  for (const [groupId, teamIds] of Object.entries(assignments)) {
    for (const teamId of teamIds) {
      inserts.push({
        group_id: groupId,
        entry_id: teamId
      });
    }
  }

  if (inserts.length > 0) {
    const { error } = await supabase
      .from('tournament_group_entries')
      .insert(inserts);

    if (error) throw error;
  }

  // Regenerate matches for affected groups
  // Delete existing group stage matches
  await supabase
    .from('tournament_matches')
    .delete()
    .eq('tournament_id', tournamentId)
    .eq('round', 'group');

  // Regenerate matches
  await generateGroupMatches(tournamentId);
};

/**
 * Generate group stage matches (round robin)
 */
export const generateGroupMatches = async (tournamentId) => {
  // Get groups with teams
  const { data: groups, error: groupError } = await supabase
    .from('tournament_groups')
    .select(`
      id,
      name,
      assigned_court,
      tournament_group_entries (
        entry_id
      )
    `)
    .eq('tournament_id', tournamentId)
    .order('sort_order');

  if (groupError) throw groupError;

  // Get tournament for best_of and courts
  const { data: tournament } = await supabase
    .from('tournaments')
    .select('default_best_of, number_of_courts, round_formats')
    .eq('id', tournamentId)
    .single();

  const bestOf = getBestOfForRound(tournament, 'group');
  const numberOfCourts = tournament?.number_of_courts || 2;

  // Delete existing group matches
  await supabase
    .from('tournament_matches')
    .delete()
    .eq('tournament_id', tournamentId)
    .eq('round', 'group');

  // Generate scheduled matches for each group
  const { scheduleGroupMatches } = await import('../utils/tournamentScheduling');
  const allMatches = [];
  const groupCount = groups.length;

  for (let i = 0; i < groups.length; i++) {
    const group = groups[i];
    const teamIds = group.tournament_group_entries.map(gt => gt.entry_id);

    const scheduledMatches = scheduleGroupMatches(
      teamIds,
      numberOfCourts,
      group.assigned_court,
      i,
      groupCount
    );

    for (const match of scheduledMatches) {
      allMatches.push({
        tournament_id: tournamentId,
        group_id: group.id,
        entry1_id: match.team1Id,
        entry2_id: match.team2Id,
        round: 'group',
        match_order: match.matchOrder,
        court_number: match.courtNumber,
        court_match_order: match.courtMatchOrder ?? null,
        best_of: bestOf,
        status: 'scheduled'
      });
    }
  }

  // Insert all matches
  const { data: insertedMatches, error: insertError } = await supabase
    .from('tournament_matches')
    .insert(allMatches)
    .select();

  if (insertError) throw insertError;

  // Update tournament status to group_stage
  await updateTournament(tournamentId, { status: 'group_stage' });

  return insertedMatches;
};

/**
 * Generate knockout bracket from group standings
 */
export const createKnockoutBracket = async (tournamentId, groupStandings) => {
  const { data: tournament } = await supabase
    .from('tournaments')
    .select('*')
    .eq('id', tournamentId)
    .single();

  if (!tournament) throw new Error('Tournament not found');
  if (!Array.isArray(groupStandings)) {
    throw new Error('createKnockoutBracket requires groupStandings array (e.g. from getAllGroupStandings)');
  }

  // Get qualified teams (progression_best_third = include best losers to fill bracket)
  const qualified = getQualifiedTeams(
    groupStandings,
    tournament.progression_top_n || 2,
    tournament.progression_best_third || false,
    tournament.best_losers_ranking_order
  );

  const bracketMatches = generateKnockoutBracket(qualified);

  // Delete existing knockout matches
  await supabase
    .from('tournament_matches')
    .delete()
    .eq('tournament_id', tournamentId)
    .in('round', ['quarter_final', 'semi_final', 'final', 'round_of_16']);

  // Insert bracket matches (per-round best_of from round_formats or default_best_of)
  const inserts = bracketMatches.map((match) => ({
    tournament_id: tournamentId,
    entry1_id: match.team1Id,
    entry2_id: match.team2Id,
    round: match.round,
    match_order: match.matchOrder,
    best_of: getBestOfForRound(tournament, match.round),
    bracket_position: `${match.bracket_position1}_vs_${match.bracket_position2}`,
    status: 'scheduled'
  }));

  const { data: insertedMatches, error } = await supabase
    .from('tournament_matches')
    .insert(inserts)
    .select();

  if (error) throw error;

  // Update tournament status to knockout
  await updateTournament(tournamentId, { status: 'knockout' });

  return insertedMatches;
};

/**
 * Regenerate knockout matches from current group standings.
 * Deletes existing knockout matches and creates new bracket using progression settings.
 */
export const regenerateKnockoutMatches = async (tournamentId) => {
  const groupStandings = await getAllGroupStandings(tournamentId);
  return createKnockoutBracket(tournamentId, groupStandings);
};

/**
 * Generate knockout bracket from manually selected teams (host chooses qualifiers)
 * @param {string} tournamentId
 * @param {Array<{teamId: string, seedLabel?: string}>} selectedTeams - Teams in seed order (1st seed first). seedLabel defaults to position.
 */
export const createKnockoutBracketFromSelectedTeams = async (tournamentId, selectedTeams, includeBronze = false) => {
  const { data: tournament } = await supabase
    .from('tournaments')
    .select('*')
    .eq('id', tournamentId)
    .single();

  if (!tournament) throw new Error('Tournament not found');
  if (!Array.isArray(selectedTeams) || selectedTeams.length === 0) {
    throw new Error('Select at least one team');
  }

  // Validate power of 2 (2, 4, 8, 16)
  const count = selectedTeams.length;
  const isPowerOf2 = count > 0 && (count & (count - 1)) === 0;
  if (!isPowerOf2) {
    throw new Error(`Select 2, 4, 8, or 16 teams for the bracket. You selected ${count}.`);
  }

  const qualified = selectedTeams.map((t, i) => ({
    teamId: t.teamId,
    seedLabel: t.seedLabel || String(i + 1),
    groupName: t.groupName || '',
    qualRank: t.qualRank ?? (i + 1),
  }));

  const bracketMatches = generateKnockoutBracket(qualified, includeBronze);

  await supabase
    .from('tournament_matches')
    .delete()
    .eq('tournament_id', tournamentId)
    .in('round', ['quarter_final', 'semi_final', 'final', 'round_of_16', 'bronze']);

  const inserts = bracketMatches.map((match) => ({
    tournament_id: tournamentId,
    entry1_id: match.team1Id,
    entry2_id: match.team2Id,
    round: match.round,
    match_order: match.matchOrder,
    best_of: getBestOfForRound(tournament, match.round),
    bracket_position: `${match.bracket_position1}_vs_${match.bracket_position2}`,
    status: 'scheduled'
  }));

  const { data: insertedMatches, error } = await supabase
    .from('tournament_matches')
    .insert(inserts)
    .select();

  if (error) throw error;

  await updateTournament(tournamentId, { status: 'knockout' });
  return insertedMatches;
};

/**
 * Advance winner to next round in knockout
 */
const advanceWinnerToNextRound = async (completedMatch, winnerTeamId) => {
  const { round, match_order, tournament_id } = completedMatch;
  
  let nextRound, nextMatchOrder, teamPosition;
  
  if (round === 'round_of_16') {
    nextRound = 'quarter_final';
    nextMatchOrder = Math.ceil(match_order / 2);
    teamPosition = match_order % 2 === 1 ? 'entry1_id' : 'entry2_id';
  } else if (round === 'quarter_final') {
    nextRound = 'semi_final';
    nextMatchOrder = Math.ceil(match_order / 2);
    teamPosition = match_order % 2 === 1 ? 'entry1_id' : 'entry2_id';
  } else if (round === 'semi_final') {
    nextRound = 'final';
    nextMatchOrder = 1;
    teamPosition = match_order === 1 ? 'entry1_id' : 'entry2_id';

    // Also advance the loser into the bronze match if it exists
    const bronzeTeamPosition = match_order === 1 ? 'entry1_id' : 'entry2_id';
    const loserId = completedMatch.entry1_id === winnerTeamId ? completedMatch.entry2_id : completedMatch.entry1_id;
    if (loserId) {
      await supabase
        .from('tournament_matches')
        .update({ [bronzeTeamPosition]: loserId })
        .eq('tournament_id', tournament_id)
        .eq('round', 'bronze')
        .eq('match_order', 1);
    }
  } else {
    return; // Final/bronze - no next match
  }
  
  // Update next match with winner
  const { error } = await supabase
    .from('tournament_matches')
    .update({ [teamPosition]: winnerTeamId })
    .eq('tournament_id', tournament_id)
    .eq('round', nextRound)
    .eq('match_order', nextMatchOrder);

  if (error) throw error;
};

/**
 * Update knockout match teams (for host to fix pairings)
 * Only updates entry1_id and entry2_id; use for scheduled matches before play
 */
export const updateKnockoutMatchTeams = async (matchId, team1Id, team2Id) => {
  const { data, error } = await supabase
    .from('tournament_matches')
    .update({
      entry1_id: team1Id || null,
      entry2_id: team2Id || null
    })
    .eq('id', matchId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Set match as walkover
 */
export const setMatchWalkover = async (tournamentMatchId, winnerTeamId) => {
  const { data: match } = await supabase
    .from('tournament_matches')
    .update({
      winner_entry_id: winnerTeamId,
      status: 'walkover'
    })
    .eq('id', tournamentMatchId)
    .select()
    .single();

  if (match?.tournament_id) {
    await checkTournamentProgression(match.tournament_id);
  }
  return match;
};

/**
 * Set match as forfeit
 */
export const setMatchForfeit = async (tournamentMatchId, winnerTeamId) => {
  const { data: match } = await supabase
    .from('tournament_matches')
    .update({
      winner_entry_id: winnerTeamId,
      status: 'forfeit'
    })
    .eq('id', tournamentMatchId)
    .select()
    .single();

  if (match?.tournament_id) {
    await checkTournamentProgression(match.tournament_id);
  }
  return match;
};

/**
 * Mark all scheduled/in-progress group matches for a withdrawn team as walkovers (opponent wins).
 * Clears any partial scores first so standings use walkover rally points (target–0) when no games exist.
 */
export const applyGroupWalkoversForWithdrawnTeam = async (tournamentId, withdrawnTeamId, clubId) => {
  const { data: rows, error } = await supabase
    .from('tournament_matches')
    .select('id, entry1_id, entry2_id, status')
    .eq('tournament_id', tournamentId)
    .eq('round', 'group')
    .in('status', ['scheduled', 'in_progress']);

  if (error) throw error;

  const affected = (rows || []).filter(
    (m) =>
      (m.entry1_id === withdrawnTeamId || m.entry2_id === withdrawnTeamId) &&
      m.entry1_id &&
      m.entry2_id
  );

  for (const m of affected) {
    const winnerId = m.entry1_id === withdrawnTeamId ? m.entry2_id : m.entry1_id;
    await recordMatchScore(m.id, [], null, clubId, null);
    await setMatchWalkover(m.id, winnerId);
  }

  return { updated: affected.length };
};

/**
 * Reset all group-stage walkovers and forfeits involving `teamId` (either side, any recorded winner).
 * Uses DB RPC (SECURITY DEFINER) so session_games cleanup and match reset succeed atomically; the old
 * client-only path could leave walkovers in place if RLS blocked a child delete after un-withdraw.
 * @param {string} clubId - Reserved for callers; RPC authorizes via tournament club + is_club_admin.
 */
export const revertWalkoversAgainstTeamInGroupStage = async (tournamentId, teamId, clubId) => {
  void clubId;
  const { data, error } = await supabase.rpc('revert_group_walkovers_for_tournament_team', {
    p_tournament_id: tournamentId,
    p_team_id: teamId
  });

  if (error) throw error;

  const n = typeof data === 'number' ? data : parseInt(data, 10);
  return { reverted: Number.isFinite(n) ? n : 0 };
};

/**
 * Advance all winners from a completed round to the next round (host-triggered)
 * Call when host clicks "Advance Winners" after all matches in a round are complete
 * @returns {string|null} The round that was advanced (e.g. 'quarter_final') or null
 */
export const advanceWinnersToNextRound = async (tournamentId) => {
  const { data: matches, error } = await supabase
    .from('tournament_matches')
    .select('id, round, match_order, winner_entry_id, status, tournament_id, entry1_id, entry2_id')
    .eq('tournament_id', tournamentId)
    .neq('round', 'group');

  if (error) throw error;

  if (!matches?.length) return null;

  const ROUNDS_WITH_NEXT = [
    { round: 'round_of_16', nextRound: 'quarter_final' },
    { round: 'quarter_final', nextRound: 'semi_final' },
    { round: 'semi_final', nextRound: 'final' },
  ];

  for (const { round, nextRound } of ROUNDS_WITH_NEXT) {
    const roundMatches = (matches || []).filter(m => m.round === round);
    const nextRoundMatches = (matches || []).filter(m => m.round === nextRound);

    if (roundMatches.length === 0 || nextRoundMatches.length === 0) continue;

    const allComplete = roundMatches.every(m => m.status === 'completed' || m.status === 'walkover' || m.status === 'forfeit');
    if (!allComplete) continue;

    // Only advance if next round's matches still need teams (skip if already populated)
    const nextRoundNeedsTeams = nextRoundMatches.some(m => !m.entry1_id || !m.entry2_id);
    if (!nextRoundNeedsTeams) continue;

    // Advance each winner
    for (const match of roundMatches) {
      const winnerId = match.winner_entry_id;
      if (winnerId) {
        await advanceWinnerToNextRound(match, winnerId);
      }
    }

    return round;
  }

  return null;
};

/**
 * Replace player on an entry (doubles substitute flow)
 */
export const replaceTeamPlayer = async (teamId, oldPlayerId, newPlayerId) => {
  const { error } = await supabase.rpc('replace_tournament_entry_player', {
    p_entry_id: teamId,
    p_old_player_id: oldPlayerId,
    p_new_player_id: newPlayerId
  });

  if (error) throw error;

  const { data: updatedTeam, error: fetchError } = await supabase
    .from('tournament_entries')
    .select(selectEntryWithPlayers)
    .eq('id', teamId)
    .single();

  if (fetchError) throw fetchError;
  return updatedTeam;
};

/**
 * Mark team as withdrawn
 */
export const withdrawTeam = async (teamId) => {
  const { data, error } = await supabase
    .from('tournament_entries')
    .update({ withdrawn_at: new Date().toISOString() })
    .eq('id', teamId)
    .select(selectEntryWithPlayers)
    .single();

  if (error) throw error;
  return data;
};

/**
 * Clear withdrawn status (host undo)
 */
export const unwithdrawTeam = async (teamId) => {
  const { data, error } = await supabase
    .from('tournament_entries')
    .update({ withdrawn_at: null })
    .eq('id', teamId)
    .select(selectEntryWithPlayers)
    .single();

  if (error) throw error;
  return data;
};

/**
 * Get all tournaments for a club
 * For completed tournaments, includes winning_team with player details
 */
export const getClubTournaments = async (clubId, seasonId = null) => {
  let query = supabase
    .from('tournaments')
    .select(`
      *,
      game_type:game_type_id (
        id,
        name,
        is_doubles
      ),
      tournament_entries (
        id,
        tournament_entry_players ( player_id )
      )
    `)
    .eq('club_id', clubId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false });

  if (seasonId) {
    query = query.eq('season_id', seasonId);
  }

  const { data: tournaments, error } = await query;

  if (error) throw error;
  if (!tournaments?.length) return tournaments;

  // Fetch winning teams for completed tournaments (from final match)
  const completedIds = tournaments.filter(t => t.status === 'completed').map(t => t.id);
  if (completedIds.length > 0) {
    const { data: finalMatches } = await supabase
      .from('tournament_matches')
      .select('tournament_id, winner_entry_id')
      .in('tournament_id', completedIds)
      .eq('round', 'final')
      .in('status', ['completed', 'walkover', 'forfeit']);

    const winnerTeamIds = [...new Set((finalMatches || []).map(m => m.winner_entry_id).filter(Boolean))];
    if (winnerTeamIds.length > 0) {
      const { data: teams } = await supabase
        .from('tournament_entries')
        .select(`
          id,
          tournament_entry_players (
            position,
            player:player_id (first_name, last_name)
          )
        `)
        .in('id', winnerTeamIds);

      const teamsById = Object.fromEntries((teams || []).map(t => [t.id, t]));
      const winnerByTournament = Object.fromEntries((finalMatches || []).map(m => [m.tournament_id, m.winner_entry_id]));

      tournaments.forEach(t => {
        if (t.status === 'completed' && winnerByTournament[t.id]) {
          t.winning_team = teamsById[winnerByTournament[t.id]];
        }
      });
    }
  }

  // Add has_scores flag (any completed/walkover/forfeit matches)
  const allIds = tournaments.map(t => t.id);
  const { data: completedMatches } = await supabase
    .from('tournament_matches')
    .select('tournament_id')
    .in('tournament_id', allIds)
    .in('status', ['completed', 'walkover', 'forfeit']);
  const tournamentIdsWithScores = new Set((completedMatches || []).map(m => m.tournament_id));
  tournaments.forEach(t => {
    t.has_scores = tournamentIdsWithScores.has(t.id);
  });

  return tournaments;
};

/**
 * Delete a tournament.
 * - Tournaments with scores (completed matches): soft delete (set deleted_at) to preserve history
 * - Tournaments without scores: hard delete (cascades to teams, groups, matches, session_games)
 */
export const deleteTournament = async (tournamentId) => {
  // Check if tournament has any completed/walkover/forfeit matches
  const { data: completedMatches } = await supabase
    .from('tournament_matches')
    .select('id')
    .eq('tournament_id', tournamentId)
    .in('status', ['completed', 'walkover', 'forfeit'])
    .limit(1);

  const hasScores = completedMatches && completedMatches.length > 0;

  if (hasScores) {
    // Soft delete - preserve data for history
    const { error } = await supabase
      .from('tournaments')
      .update({ deleted_at: new Date().toISOString(), updated_at: new Date().toISOString() })
      .eq('id', tournamentId);

    if (error) throw error;
  } else {
    // Hard delete - no meaningful data to preserve
    const { error } = await supabase
      .from('tournaments')
      .delete()
      .eq('id', tournamentId);

    if (error) throw error;
  }
};

/**
 * Clear all scores for a match (reset to no games played).
 * Deletes session_games and sets match status to scheduled.
 */
const clearMatchScore = async (matchId) => {
  const { data: existingGames, error: fetchError } = await supabase
    .from('session_games')
    .select('id')
    .eq('tournament_match_id', matchId);

  if (fetchError) throw fetchError;

  const gameIds = (existingGames || []).map(g => g.id);
  if (gameIds.length > 0) {
    await supabase.from('players_session_games').delete().in('session_game_id', gameIds);
    await supabase.from('partnership_games').delete().in('session_game_id', gameIds);
    await supabase.from('elo_history').delete().in('session_game_id', gameIds);
    await supabase.from('session_games').delete().eq('tournament_match_id', matchId);
  }

  const { data: updated, error } = await supabase
    .from('tournament_matches')
    .update({ status: 'scheduled', winner_entry_id: null })
    .eq('id', matchId)
    .select()
    .single();

  if (error) throw error;
  return updated;
};

/**
 * Record match score and create session games.
 * Supports partial saves: pass winnerTeamId=null when match is not yet complete (e.g. 1 game of Best of 3).
 * Pass games=[] to clear all scores and reset the match.
 * @param {string} matchId - Tournament match ID
 * @param {Array} games - Array of game results [{team1Score, team2Score, winner}, ...]
 * @param {string|null} winnerTeamId - ID of winning team, or null for partial save
 * @param {string} clubId - Club ID
 * @param {string} sessionId - Optional session ID if match is played in a session
 */
export const recordMatchScore = async (matchId, games, winnerTeamId, clubId, sessionId = null) => {
  // Allow empty games to clear/reset a match (e.g. after deleting all games)
  if (!Array.isArray(games)) throw new Error('Games must be an array');

  if (games.length === 0) {
    return clearMatchScore(matchId);
  }

  // Get match details
  const { data: match, error: matchError } = await supabase
    .from('tournament_matches')
    .select(`
      *,
      tournament:tournament_id (
        id,
        game_type_id,
        season_id
      ),
      team1:entry1_id (
        *,
        tournament_entry_players (
          position,
          player:player_id (*)
        )
      ),
      team2:entry2_id (
        *,
        tournament_entry_players (
          position,
          player:player_id (*)
        )
      )
    `)
    .eq('id', matchId)
    .single();

  if (matchError) throw matchError;

  // Delete existing session_games for this match (handles partial save updates and edits)
  const { data: existingGames, error: fetchError } = await supabase
    .from('session_games')
    .select('id')
    .eq('tournament_match_id', matchId);

  if (fetchError) throw fetchError;

  const gameIds = (existingGames || []).map(g => g.id);
  if (gameIds.length > 0) {
    await supabase.from('players_session_games').delete().in('session_game_id', gameIds);
    await supabase.from('partnership_games').delete().in('session_game_id', gameIds);
    await supabase.from('elo_history').delete().in('session_game_id', gameIds);
    await supabase.from('session_games').delete().eq('tournament_match_id', matchId);
  }

  // Create session games for each game played
  const { createGameDirect } = await import('./sessionService');

  for (let i = 0; i < games.length; i++) {
    const game = games[i];

    const team1Players = entryPlayersForSessionGame(match.team1);
    const team2Players = entryPlayersForSessionGame(match.team2);

    await createGameDirect({
      sessionId: sessionId,
      clubId: clubId,
      seasonId: match.tournament.season_id,
      gameTypeId: match.tournament.game_type_id,
      team1Score: game.team1Score,
      team2Score: game.team2Score,
      winningTeam: game.winner,
      team1Players: team1Players,
      team2Players: team2Players,
      gameCreateMethod: 'tournament',
      tournamentMatchId: matchId,
      gameOrder: i + 1,
      startTime: new Date(),
      endTime: new Date()
    });
  }

  // Update match: completed only when we have a winner
  const updatePayload = winnerTeamId
    ? { status: 'completed', winner_entry_id: winnerTeamId }
    : { winner_entry_id: null };

  const { data: updatedMatch, error: updateError } = await supabase
    .from('tournament_matches')
    .update(updatePayload)
    .eq('id', matchId)
    .select()
    .single();

  if (updateError) throw updateError;

  if (winnerTeamId) {
    await checkTournamentProgression(match.tournament_id);
  }

  return updatedMatch;
};

/**
 * Update match score - deletes existing games and re-records
 * Use when correcting an incorrectly entered score
 */
export const updateMatchScore = async (matchId, games, winnerTeamId, clubId, sessionId = null) => {
  return recordMatchScore(matchId, games, winnerTeamId, clubId, sessionId);
};

/**
 * Check and update tournament progression
 * Called after a match is completed to see if stage is done
 */
const checkTournamentProgression = async (tournamentId) => {
  // Get tournament with matches
  const { data: tournament } = await supabase
    .from('tournaments')
    .select(`
      *,
      tournament_matches (
        id,
        round,
        status
      )
    `)
    .eq('id', tournamentId)
    .single();

  if (!tournament) return;

  // Group stage completion: host manually generates knockout bracket (no auto-generation)
  
  // Knockout: auto-advance winners when a round completes
  if (tournament.status === 'knockout') {
    await advanceWinnersToNextRound(tournamentId);
    const knockoutMatches = tournament.tournament_matches.filter(m => m.round !== 'group');
    const allKnockoutMatchesComplete = knockoutMatches.every(m =>
      ['completed', 'walkover', 'forfeit'].includes(m.status)
    );
    
    if (allKnockoutMatchesComplete && knockoutMatches.length > 0) {
      // Tournament is complete
      await supabase
        .from('tournaments')
        .update({ status: 'completed' })
        .eq('id', tournamentId);
    }
  }
};

